import { fetchIncomeStApi } from "../api/fetchIncomeStApi";

export const toNumber = (value) => {
  if (typeof value === "number") return value;
  if (value === undefined || value === null || value === "") return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export function parseRow(row) {
  const raw = String(row.ACCOUNTNAME ?? "");
  const trimmed = raw.trim();
  if (trimmed.startsWith("***"))
    return { kind: "group", label: trimmed.slice(3).trim() };
  if (trimmed.startsWith("**"))
    return {
      kind: "subtotal",
      label: trimmed.slice(2).trim(),
      amount: toNumber(row.SUMGROUPAMOUNT),
    };
  return {
    kind: "account",
    label: trimmed,
    amount: toNumber(row.SUMACCOUNTAMOUNT ?? row.INCOME ?? row.EXPENSE),
  };
}

/**
 * Merges one or more periods into a single ordered layout.
 * periods: [{ label, rows }]. Accounts present in only some periods get 0 elsewhere.
 * Returns { columns: string[], lines: [{ kind, label, group, values: number[] }] }.
 */
export function buildStatement(periods) {
  const lines = [];
  const index = new Map();
  periods.forEach((period, col) => {
    let group = "";
    let lastPos = -1; // position of the previous row of this period in the merged layout
    period.rows.forEach((row) => {
      const p = parseRow(row);
      if (p.kind === "group") group = p.label;
      const key = `${p.kind}|${p.kind === "group" ? "" : group}|${p.label.toLowerCase()}`;
      let line = index.get(key);
      if (!line) {
        line = {
          kind: p.kind,
          label: p.label,
          group,
          values: periods.map(() => 0),
        };
        lines.splice(lastPos + 1, 0, line);
        index.set(key, line);
      }
      if (p.kind !== "group") line.values[col] = p.amount;
      lastPos = lines.indexOf(line);
    });
  });
  return { columns: periods.map((p) => p.label), lines };
}

// /** Converts [{ "Sep 2026": rows }, ...] into [{ label, rows }]. */
// export const monthlyToPeriods = (monthly) =>
//   monthly.flatMap((entry) =>
//     Object.entries(entry).map(([label, rows]) => ({ label, rows })),
//   );

export const monthlyToPeriods = (monthly) => {
  if (!monthly) return [];

  return Object.entries(monthly).map(([label, rows]) => ({
    label,
    rows,
  }));
};

/** Finds a subtotal value by name fragment (e.g. "total income"). */
export function subtotal(statement, fragment, col) {
  const line = statement.lines.find(
    (l) => l.kind === "subtotal" && l.label.toLowerCase().includes(fragment),
  );
  return line ? line.values[col] : 0;
}

function getMonthsBetweenDates(startDateStr, endDateStr) {
  // Universal parser: Handles both hyphens (YYYY-MM-DD) and slashes (DD/MM/YYYY)
  const parseDate = (str) => {
    // If it contains a hyphen, assume ISO format: YYYY-MM-DD
    if (str.includes("-")) {
      const [year, month, day] = str.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    // Otherwise, assume slash format: DD/MM/YYYY
    else {
      const [day, month, year] = str.split("/").map(Number);
      return new Date(year, month - 1, day);
    }
  };

  // Helper to format Date object into YYYY-MM-DD string
  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const start = parseDate(startDateStr);
  const end = parseDate(endDateStr);

  const results = [];
  let current = new Date(start.getFullYear(), start.getMonth(), 1);

  while (current <= end) {
    const year = current.getFullYear();
    const month = current.getMonth();

    // Start of the month segment
    let monthStart = new Date(year, month, 1);
    if (monthStart < start) {
      monthStart = new Date(start.getTime());
    }

    // End of the month segment
    let monthEnd = new Date(year, month + 1, 0); // Last day of the current month
    if (monthEnd > end) {
      monthEnd = new Date(end.getTime());
    }

    // Generate label (e.g., "Aug 2026")
    const label = monthStart.toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    });

    results.push({
      label: label,
      startDate: formatDate(monthStart),
      endDate: formatDate(monthEnd),
    });

    // Move to the next month
    current.setMonth(current.getMonth() + 1);
  }

  return results;
}

const parseLabelToDate = (label) => {
  const [monthStr, yearStr] = label.split(" ");
  // Normalize "Sept" to "Sep" so JavaScript can understand it
  const cleanMonth = monthStr.substring(0, 3);

  const months = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };

  return new Date(parseInt(yearStr, 10), months[cleanMonth], 1);
};

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @returns {Promise<[]>}
 */
export async function getIncomeStatement({ startDate, endDate }) {
  let data = [];
  try {
    const formattedStart = new Date(startDate).toLocaleDateString("en-CA");
    const formattedEnd = new Date(endDate).toLocaleDateString("en-CA");

    data = await fetchIncomeStApi(formattedStart, formattedEnd);
    // console.log(sales);
  } catch (error) {}
  return data;
}

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @returns {Promise<{}>}
 */
export async function getMonthlyIncomeStatement({ startDate, endDate }) {
  let data = {};
  // get months in the start and end date
  // e.g. [{label: 'Jun 2026', startDate: '2026-06-01', endDate: '2026-06-30'}]

  let fetched_dates = getMonthsBetweenDates(startDate, endDate);

  try {
    if (fetched_dates.length > 1) {
      await Promise.all(
        fetched_dates.map(async (f) => {
          const formattedStart = new Date(f.startDate).toLocaleDateString(
            "en-CA",
          );
          const formattedEnd = new Date(f.endDate).toLocaleDateString("en-CA");

          // Fetch and assign directly
          data[f.label] = await fetchIncomeStApi(formattedStart, formattedEnd);
        }),
      );
    }
  } catch (error) {}
  // console.log(data);

  const sortedEntries = Object.entries(data).sort(([keyA], [keyB]) => {
    return parseLabelToDate(keyA) - parseLabelToDate(keyB);
  });

  const sortedMap = Object.fromEntries(sortedEntries);

  return sortedMap;
}
